import { Injectable, inject } from '@angular/core';
import {
  NULL_UUID,
  PENDING_SHARED_STORY_IMPORT,
  SELECTED_PROFILE_ID,
} from '@storees/shared-constants';
import { SessionService } from './session.service';
import { AuthService } from './auth-service';
import { StoreesBackendService } from './storees-backend.service';
import { DexieProfilesService } from './dexie/dexie-profiles.service';
import { ProfileService } from './profile-service';
import { logger } from '../utils/logger';

export type SharedStoryImportAfterLoadResult =
  | { outcome: 'imported'; entryStoryId: string }
  | { outcome: 'failed' }
  | { outcome: 'queued_session' }
  | { outcome: 'queued_profile' };

@Injectable({ providedIn: 'root' })
export class StoryShareImportService {
  private sessionService = inject(SessionService);
  private authService = inject(AuthService);
  private storeesBackendService = inject(StoreesBackendService);
  private dexieProfilesService = inject(DexieProfilesService);
  private profileService = inject(ProfileService);

  private async resolveSelectedProfileId(): Promise<string | null> {
    await this.profileService.repairSelectedProfileIfInvalid();
    const profileId = await this.sessionService.getPersistent(SELECTED_PROFILE_ID);
    if (!profileId || profileId === NULL_UUID) {
      return null;
    }
    return profileId;
  }

  async setPendingImport(sourceStoryId: string): Promise<void> {
    const payload = {
      sourceStoryId,
      createdAtIso: new Date().toISOString(),
    };
    await this.sessionService.setPersistent(
      PENDING_SHARED_STORY_IMPORT,
      JSON.stringify(payload),
    );
    console.log('[IMPORT_SHARED_STORY] queued pending import', payload);
  }

  async getPendingImport(): Promise<{
    sourceStoryId: string;
    createdAtIso: string;
  } | null> {
    const raw = await this.sessionService.getPersistent(
      PENDING_SHARED_STORY_IMPORT,
    );
    if (!raw || typeof raw !== 'string' || raw.trim() === '') {
      return null;
    }
    try {
      const o = JSON.parse(raw) as {
        sourceStoryId?: string;
        createdAtIso?: string;
      };
      if (!o.sourceStoryId || typeof o.sourceStoryId !== 'string') {
        return null;
      }
      return {
        sourceStoryId: o.sourceStoryId,
        createdAtIso: o.createdAtIso ?? '',
      };
    } catch {
      return null;
    }
  }

  async clearPendingImport(): Promise<void> {
    await this.sessionService.setPersistent(PENDING_SHARED_STORY_IMPORT, '');
    console.log('[IMPORT_SHARED_STORY] cleared pending import queue');
  }

  /**
   * Ensure Dexie has a Storees API JWT: Ory-tokenized when logged in, otherwise
   * anonymous JWT from {@link StoreesBackendService.getAnonymousToken}.
   * Ory `/sessions/whoami` may return 401 while this still yields a valid anonymous JWT.
   */
  private async ensureStoreesApiJwtThenGet(): Promise<string> {
    await this.authService.updateSessionJWT();
    return (await this.authService.getSessionJwtToken()).trim();
  }

  /**
   * After public share assets load: import immediately if JWT + real profile;
   * otherwise persist pending for retry after sign-in / profile selection.
   */
  async importOrQueueAfterShareLoad(
    sourceStoryId: string,
  ): Promise<SharedStoryImportAfterLoadResult> {
    const profileId = await this.resolveSelectedProfileId();
    const jwtToken = await this.ensureStoreesApiJwtThenGet();
    const hasProfile = !!profileId && profileId !== NULL_UUID;
    const hasJwt = !!jwtToken;

    const jwtAnonymous =
      hasJwt && this.authService.isAnonymousJwtPayload(jwtToken);
    logger.warn('[IMPORT_SHARED_STORY] importOrQueueAfterShareLoad gate', {
      sourceStoryId,
      hasJwt,
      jwtAnonymous,
      hasProfile,
      selectedProfileSuffix:
        profileId && profileId !== NULL_UUID ? profileId.slice(-12) : profileId,
    });

    if (hasJwt && hasProfile) {
      try {
        console.log(
          '[IMPORT_SHARED_STORY] calling POST /v1/stories/import-shared-story',
          { profileId, sourceStoryId },
        );
        const result = await this.storeesBackendService.importSharedStory(
          jwtToken,
          profileId,
          sourceStoryId,
        );
        console.log(
          '[IMPORT_SHARED_STORY] import OK — syncing profile story lists',
          result,
        );
        await this.dexieProfilesService.syncProfileStoryLists(jwtToken);
        this.profileService.refreshProfiles();
        console.log('[IMPORT_SHARED_STORY] Dexie profile story lists refreshed');
        await this.clearPendingImport();
        logger.warn('[IMPORT_SHARED_STORY] importOrQueueAfterShareLoad imported OK', {
          sourceStoryId,
          profileIdSuffix: profileId.slice(-12),
          entryStoryId: result.entryStoryId,
        });
        return { outcome: 'imported', entryStoryId: result.entryStoryId };
      } catch (err: unknown) {
        logger.warn('[IMPORT_SHARED_STORY] importOrQueueAfterShareLoad HTTP path FAILED', {
          sourceStoryId,
          profileId,
          error: err,
        });
        console.error('[IMPORT_SHARED_STORY] import FAILED', {
          sourceStoryId,
          profileId,
          error: err,
        });
        await this.setPendingImport(sourceStoryId);
        return { outcome: 'failed' };
      }
    }

    logger.warn('[IMPORT_SHARED_STORY] importOrQueueAfterShareLoad queue only (no POST)', {
      sourceStoryId,
      hasJwt,
      jwtAnonymous: hasJwt && this.authService.isAnonymousJwtPayload(jwtToken),
      hasProfile,
      branch: !hasJwt ? 'queued_session' : 'queued_profile',
    });
    await this.setPendingImport(sourceStoryId);
    if (!hasJwt) {
      return { outcome: 'queued_session' };
    }
    return { outcome: 'queued_profile' };
  }

  /**
   * Run when JWT and/or profile may have appeared (app open, landing, re-enter player).
   * Uses queued `sourceStoryId` only.
   */
  async tryFlushPendingImport(): Promise<{
    flushed: boolean;
    entryStoryId?: string;
  }> {
    const pending = await this.getPendingImport();
    if (!pending) {
      return { flushed: false };
    }
    const jwtToken = await this.ensureStoreesApiJwtThenGet();
    const profileId = await this.resolveSelectedProfileId();
    const hasProfile = !!profileId && profileId !== NULL_UUID;
    const hasJwt = !!jwtToken;

    logger.warn('[IMPORT_SHARED_STORY] tryFlushPendingImport', {
      pendingSource: pending.sourceStoryId,
      hasJwt,
      jwtAnonymous:
        hasJwt && this.authService.isAnonymousJwtPayload(jwtToken),
      hasProfile,
      selectedProfileSuffix:
        profileId && profileId !== NULL_UUID ? profileId.slice(-12) : profileId,
    });

    if (!hasJwt || !hasProfile) {
      logger.warn(
        '[IMPORT_SHARED_STORY] tryFlushPendingImport skip — credentials not ready',
      );
      return { flushed: false };
    }
    try {
      console.log(
        '[IMPORT_SHARED_STORY] flushing pending import',
        pending.sourceStoryId,
      );
      const importBody = await this.storeesBackendService.importSharedStory(
        jwtToken,
        profileId,
        pending.sourceStoryId,
      );
      await this.dexieProfilesService.syncProfileStoryLists(jwtToken);
      this.profileService.refreshProfiles();
      await this.clearPendingImport();
      logger.warn('[IMPORT_SHARED_STORY] tryFlushPendingImport OK', {
        entryStoryId: importBody.entryStoryId,
      });
      console.log('[IMPORT_SHARED_STORY] pending flush OK');
      return { flushed: true, entryStoryId: importBody.entryStoryId };
    } catch (err: unknown) {
      logger.warn('[IMPORT_SHARED_STORY] tryFlushPendingImport FAILED', { error: err });
      console.error('[IMPORT_SHARED_STORY] pending flush FAILED', err);
      return { flushed: false };
    }
  }
}
